import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

interface DropdownMenuProps {
  children: React.ReactNode;
}

interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  className?: string;
}

interface DropdownMenuContentProps {
  children: React.ReactNode;
  className?: string;
  align?: 'start' | 'end';
  /** Render inside the trigger wrapper instead of portaling to document.body (required inside modals). */
  disablePortal?: boolean;
}

interface DropdownMenuItemProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}

interface DropdownMenuSeparatorProps {
  className?: string;
}

const DropdownMenuContext = React.createContext<{
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wrapperRef: React.RefObject<HTMLDivElement>;
  contentRef: React.RefObject<HTMLDivElement>;
}>({
  isOpen: false,
  setIsOpen: () => {},
  wrapperRef: { current: null },
  contentRef: { current: null },
});

export const DropdownMenu: React.FC<DropdownMenuProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapperRef.current?.contains(target) ||
        contentRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    const handleScroll = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  return (
    <DropdownMenuContext.Provider value={{ isOpen, setIsOpen, wrapperRef, contentRef }}>
      <div ref={wrapperRef} className="relative inline-block">
        {children}
      </div>
    </DropdownMenuContext.Provider>
  );
};

export const DropdownMenuTrigger: React.FC<DropdownMenuTriggerProps> = ({ children, className }) => {
  const { isOpen, setIsOpen } = React.useContext(DropdownMenuContext);

  return (
    <button
      type="button"
      className={cn(className)}
      onClick={() => setIsOpen(!isOpen)}
      aria-haspopup="true"
      aria-expanded={isOpen ? 'true' : 'false'}
    >
      {children}
    </button>
  );
};

export const DropdownMenuContent: React.FC<DropdownMenuContentProps> = ({
  children,
  className,
  align = 'end',
  disablePortal = false,
}) => {
  const { isOpen, wrapperRef, contentRef } = React.useContext(DropdownMenuContext);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!isOpen || !wrapperRef.current || disablePortal) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      if (!wrapperRef.current) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      const menuWidth = contentRef.current?.offsetWidth ?? 192;
      const menuHeight = contentRef.current?.offsetHeight ?? 160;
      const gap = 8;

      let left = align === 'end' ? rect.right - menuWidth : rect.left;
      left = Math.max(gap, Math.min(left, window.innerWidth - menuWidth - gap));

      let top = rect.bottom + gap;
      if (top + menuHeight > window.innerHeight - gap) {
        top = Math.max(gap, rect.top - menuHeight - gap);
      }

      setPosition({ top, left });
    };

    updatePosition();
    requestAnimationFrame(updatePosition);
  }, [isOpen, align, wrapperRef, contentRef, children, disablePortal]);

  if (!isOpen) return null;

  const menuContent = (
    <div
      ref={contentRef}
      style={
        disablePortal
          ? undefined
          : { position: 'fixed', top: position?.top, left: position?.left }
      }
      className={cn(
        'z-[200] w-max min-w-[12rem] max-w-[18rem] rounded-md border border-[#E2E5EA] bg-white p-1 shadow-lg whitespace-nowrap',
        disablePortal && (align === 'end' ? 'absolute right-0 top-[calc(100%+8px)]' : 'absolute left-0 top-[calc(100%+8px)]'),
        className
      )}
    >
      {children}
    </div>
  );

  if (disablePortal) {
    return menuContent;
  }

  if (!position) return null;

  return createPortal(menuContent, document.body);
};

export const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({ children, className, onClick, disabled }) => {
  const { setIsOpen } = React.useContext(DropdownMenuContext);

  const handleClick = () => {
    if (disabled) return;
    onClick?.();
    setIsOpen(false);
  };

  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center justify-start text-left rounded-md px-2 py-1.5 text-[13px] text-text-grey outline-none transition-colors hover:bg-page-bg focus:bg-page-bg disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      onClick={handleClick}
    >
      {children}
    </button>
  );
};

export const DropdownMenuSeparator: React.FC<DropdownMenuSeparatorProps> = ({ className }) => {
  return (
    <div className={cn('-mx-1 my-1 h-px bg-gray-100', className)} />
  );
};
